import { calculateItemTotalPrice as calculateItemPrice } from '@/utils/priceCalculations';
const generateLocalProductId = (item) => {
    const baseId = `${item.id}_${item.selectedSize?.id || 'no-size'}`;
    const ingredientsHash = (item.Ingredients || [])
        .filter(ing => !ing.isDefault && ing.quantity > 0)
        .map(ing => `${ing.id}_${ing.quantity}`)
        .sort()
        .join('_');
    const supplementsHash = (item.supplements || [])
        .filter(sup => sup.quantity > 0)
        .map(sup => `${sup.id}_${sup.quantity}`)
        .sort()
        .join('_');
    return `${baseId}_${ingredientsHash}_${supplementsHash}`;
};
const cart = {
    namespaced: true,
    state: () => ({
        cart: [],
    }),
    mutations: {
        SET_CART(state, cart) {
            state.cart = cart;
        },
        ADD_TO_CART(state, { item, totalPrice }) {
            const existingItemIndex = state.cart.findIndex((i) => i.localProductId === item.localProductId);
            if (existingItemIndex !== -1) {
                state.cart[existingItemIndex].quantity += 1;
                state.cart[existingItemIndex].totalPrice = totalPrice * state.cart[existingItemIndex].quantity;
            }
            else {
                state.cart.push({
                    ...item,
                    quantity: item.quantity || 1,
                    totalPrice: totalPrice,
                    localProductId: generateLocalProductId(item)
                });
            }
        },
        REMOVE_FROM_CART(state, item) {
            state.cart = state.cart.filter((i) => i.localProductId !== item.localProductId);
        },
        CLEAR_CART(state) {
            state.cart = [];
        },
        INCREMENT_QUANTITY(state, { itemId, totalPrice }) {
            const item = state.cart.find(item => item.localProductId === itemId);
            if (item) {
                item.quantity += 1;
                item.totalPrice = totalPrice * item.quantity;
            }
        },
        DECREMENT_QUANTITY(state, { itemId, totalPrice }) {
            const item = state.cart.find(item => item.localProductId === itemId);
            if (item) {
                item.quantity = Math.max(1, item.quantity - 1);
                item.totalPrice = totalPrice * item.quantity;
            }
        },
        UPDATE_QUANTITY(state, { itemId, quantity, totalPrice }) {
            const item = state.cart.find(item => item.localProductId === itemId);
            if (item) {
                item.quantity = Math.max(1, quantity);
                item.totalPrice = totalPrice * item.quantity;
            }
        },
        UPDATE_ITEM_INGREDIENTS(state, { itemId, ingredients, totalPrice }) {
            const item = state.cart.find(item => item.localProductId === itemId);
            if (item) {
                item.Ingredients = ingredients;
                item.ingredients = ingredients;
                item.totalPrice = totalPrice * item.quantity;
            }
        },
        UPDATE_ITEM_SUPPLEMENTS(state, { itemId, supplements, totalPrice }) {
            const item = state.cart.find(item => item.localProductId === itemId);
            if (item) {
                item.supplements = supplements;
                item.totalPrice = totalPrice * item.quantity;
            }
        }
    },
    actions: {
        calculateItemTotalPrice({ rootGetters }, { item }) {
            const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
            return calculateItemPrice(item, currentOrderType);
        },
        async addToCart({ commit, dispatch }, { item }) {
            const originalSize = {
                ...item.selectedSize,
                price: item?.selectedSize?.price,
                priceLivraison: item?.selectedSize?.priceLivraison
            };
            const itemWithOriginalSize = {
                ...item,
                selectedSize: originalSize
            };
            const totalPrice = await dispatch('calculateItemTotalPrice', { item: itemWithOriginalSize });
            commit('ADD_TO_CART', { item: itemWithOriginalSize, totalPrice });
            dispatch('saveToStorage');
        },
        removeFromCart({ commit, dispatch }, item) {
            commit('REMOVE_FROM_CART', item);
            dispatch('saveToStorage');
        },
        clearCart({ commit }) {
            commit('CLEAR_CART');
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('cart');
            }
        },
        async incrementQuantity({ commit, dispatch, getters }, itemId) {
            const item = getters.getItemById(itemId);
            if (item) {
                const totalPrice = await dispatch('calculateItemTotalPrice', { item });
                commit('INCREMENT_QUANTITY', { itemId, totalPrice });
                dispatch('saveToStorage');
            }
        },
        async decrementQuantity({ commit, dispatch, getters }, itemId) {
            const item = getters.getItemById(itemId);
            if (item) {
                const totalPrice = await dispatch('calculateItemTotalPrice', { item });
                commit('DECREMENT_QUANTITY', { itemId, totalPrice });
                dispatch('saveToStorage');
            }
        },
        async updateQuantity({ commit, dispatch, getters }, { itemId, quantity }) {
            const item = getters.getItemById(itemId);
            if (item && quantity > 0) {
                const totalPrice = await dispatch('calculateItemTotalPrice', { item });
                commit('UPDATE_QUANTITY', { itemId, quantity, totalPrice });
                dispatch('saveToStorage');
            }
            else if (item && quantity <= 0) {
                commit('REMOVE_FROM_CART', item);
                dispatch('saveToStorage');
            }
        },
        async recalculateAllPrices({ commit, dispatch, getters }) {
            const updatedCart = [];
            for (const item of getters.cart) {
                const totalPrice = await dispatch('calculateItemTotalPrice', { item });
                updatedCart.push({
                    ...item,
                    totalPrice: totalPrice
                });
            }
            commit('SET_CART', updatedCart);
            dispatch('saveToStorage');
        },
        addIngredient({ commit, dispatch, getters, rootGetters }, { itemId, ingredient }) {
            const item = getters.getItemById(itemId);
            if (item) {
                const itemIngredients = item.Ingredients || item.ingredients || [];
                const updatedIngredients = itemIngredients.some((i) => i.name === ingredient.name)
                    ? itemIngredients.map((i) => i.name === ingredient.name ? { ...i, quantity: ingredient.quantity } : i)
                    : [...itemIngredients, ingredient];
                const updatedItem = { ...item, Ingredients: updatedIngredients, ingredients: updatedIngredients };
                const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
                const totalPrice = calculateItemPrice(updatedItem, currentOrderType);
                commit('UPDATE_ITEM_INGREDIENTS', { itemId, ingredients: updatedIngredients, totalPrice });
                dispatch('saveToStorage');
            }
        },
        removeIngredient({ commit, dispatch, getters, rootGetters }, { itemId, ingredient }) {
            const item = getters.getItemById(itemId);
            if (item) {
                const itemIngredients = item.Ingredients || item.ingredients || [];
                const updatedIngredients = ingredient.quantity <= 0
                    ? itemIngredients.filter((i) => i.name !== ingredient.name)
                    : itemIngredients.map((i) => i.name === ingredient.name ? { ...i, quantity: ingredient.quantity } : i);
                const updatedItem = { ...item, Ingredients: updatedIngredients, ingredients: updatedIngredients };
                const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
                const totalPrice = calculateItemPrice(updatedItem, currentOrderType);
                commit('UPDATE_ITEM_INGREDIENTS', { itemId, ingredients: updatedIngredients, totalPrice });
                dispatch('saveToStorage');
            }
        },
        clearIngredients({ commit, dispatch, getters, rootGetters }, itemId) {
            const item = getters.getItemById(itemId);
            if (item) {
                const itemIngredients = item.Ingredients || item.ingredients || [];
                const updatedIngredients = itemIngredients.filter((i) => i.isDefault);
                const updatedItem = { ...item, Ingredients: updatedIngredients, ingredients: updatedIngredients };
                const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
                const totalPrice = calculateItemPrice(updatedItem, currentOrderType);
                commit('UPDATE_ITEM_INGREDIENTS', { itemId, ingredients: updatedIngredients, totalPrice });
                dispatch('saveToStorage');
            }
        },
        addSupplement({ commit, dispatch, getters, rootGetters }, { itemId, supplement }) {
            const item = getters.getItemById(itemId);
            if (item) {
                const currentSupplements = item.supplements || [];
                const updatedSupplements = currentSupplements.some((s) => s.name === supplement.name)
                    ? currentSupplements.map((s) => s.name === supplement.name ? { ...s, quantity: supplement.quantity } : s)
                    : [...currentSupplements, supplement];
                const updatedItem = { ...item, supplements: updatedSupplements };
                const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
                const totalPrice = calculateItemPrice(updatedItem, currentOrderType);
                commit('UPDATE_ITEM_SUPPLEMENTS', { itemId, supplements: updatedSupplements, totalPrice });
                dispatch('saveToStorage');
            }
        },
        removeSupplement({ commit, dispatch, getters, rootGetters }, { itemId, supplement }) {
            const item = getters.getItemById(itemId);
            if (item) {
                const currentSupplements = item.supplements || [];
                const updatedSupplements = supplement.quantity <= 0
                    ? currentSupplements.filter((s) => s.name !== supplement.name)
                    : currentSupplements.map((s) => s.name === supplement.name ? { ...s, quantity: supplement.quantity } : s);
                const updatedItem = { ...item, supplements: updatedSupplements };
                const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
                const totalPrice = calculateItemPrice(updatedItem, currentOrderType);
                commit('UPDATE_ITEM_SUPPLEMENTS', { itemId, supplements: updatedSupplements, totalPrice });
                dispatch('saveToStorage');
            }
        },
        clearSupplements({ commit, dispatch, getters, rootGetters }, itemId) {
            const item = getters.getItemById(itemId);
            if (item) {
                const updatedItem = { ...item, supplements: [] };
                const currentOrderType = rootGetters['orderType/selectedOrderType'] || 'dine_in';
                const totalPrice = calculateItemPrice(updatedItem, currentOrderType);
                commit('UPDATE_ITEM_SUPPLEMENTS', { itemId, supplements: [], totalPrice });
                dispatch('saveToStorage');
            }
        },
        loadFromStorage({ commit }) {
            if (typeof localStorage === 'undefined')
                return;
            const value = localStorage.getItem('cart');
            if (value) {
                try {
                    const parsed = JSON.parse(value);
                    if (parsed.state && Array.isArray(parsed.state.cart)) {
                        commit('SET_CART', parsed.state.cart);
                    }
                }
                catch (error) {
                    console.error('Erreur lors du chargement du panier depuis le stockage local:', error);
                }
            }
        },
        saveToStorage({ state }) {
            if (typeof localStorage === 'undefined')
                return;
            try {
                const cartData = {
                    state: {
                        cart: state.cart
                    }
                };
                const value = JSON.stringify(cartData);
                localStorage.setItem('cart', value);
            }
            catch (error) {
                console.error('Erreur lors de la sauvegarde du panier dans le stockage local:', error);
            }
        }
    },
    getters: {
        cart: (state) => state.cart,
        cartCount: (state) => state.cart.reduce((count, item) => count + item.quantity, 0),
        cartTotal: (state) => state.cart.reduce((total, item) => total + (item.totalPrice || 0), 0),
        getItemById: (state) => (itemId) => state.cart.find(item => item.localProductId === itemId),
        isEmpty: (state) => state.cart.length === 0
    }
};
export default cart;
